import React, { useState, useEffect } from 'react';
import * as d3 from 'd3';
import { Search, Filter, ChevronDown, Users, X, Maximize2, Minimize2, Briefcase, Heart, Home, Globe, Coffee, Book, DollarSign } from 'lucide-react';
import LoadingState from '../common/LoadingState';

const ClientRelationshipGraph = ({ selectedClient }) => {
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredData, setFilteredData] = useState({ nodes: [], links: [] });
  const [selectedRelationshipTypes, setSelectedRelationshipTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [focusedClient, setFocusedClient] = useState(null);
  const [networkStats, setNetworkStats] = useState({
    totalConnections: 0,
    totalNodes: 0,
    avgConnectionsPerClient: 0,
    mostConnectedClient: null,
    strongestRelationship: null,
    relationshipCounts: {},
    connectionCounts: {}
  });
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [highlightedNode, setHighlightedNode] = useState(null);
  const [highlightedConnections, setHighlightedConnections] = useState([]);

  const relationshipTypes = [
    'Family', 'Business', 'Friend', 'Professional',
    'Client Referral', 'Advisor', 'Investment Partner',
    'Charity', 'Club Member', 'Education'
  ];

  // More distinct color palette
  const colorScale = d3.scaleOrdinal()
    .domain(relationshipTypes)
    .range([
      '#e41a1c', '#377eb8', '#4daf4a', '#984ea3',
      '#ff7f00', '#a65628', '#f781bf', '#999999',
      '#66c2a5', '#fc8d62'
    ]);

  // Define focusOnClient function before it's used
  const focusOnClient = (clientId) => {
    if (!clientId) return;

    setFocusedClient(clientId);

    // Filter nodes and links to show only the focused client and direct connections
    const connectedNodes = new Set([clientId]);
    const connectedLinks = [];

    graphData.links.forEach(link => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;

      if (sourceId === clientId || targetId === clientId) {
        connectedNodes.add(sourceId);
        connectedNodes.add(targetId);
        connectedLinks.push(link);
      }
    });

    const focusedNodes = graphData.nodes.filter(node => connectedNodes.has(node.id));

    setFilteredData({ nodes: focusedNodes, links: connectedLinks });

    // Trigger graph redraw
    setTimeout(() => {
      renderGraph(focusedNodes, connectedLinks);
    }, 100);
  };

  const resetFocus = () => {
    setFocusedClient(null);
    setSearchQuery('');
    setSelectedRelationshipTypes([]);
    setFilteredData({ nodes: graphData.nodes, links: graphData.links });

    // Trigger graph redraw
    setTimeout(() => {
      renderGraph(graphData.nodes, graphData.links);
    }, 100);
  };

  // Handle clicks outside search results and filters
  useEffect(() => {
    function handleClickOutside(event) {
      // Close search results if clicking outside search area
      const searchElement = document.getElementById('search-container');
      if (searchElement && !searchElement.contains(event.target)) {
        setShowSearchResults(false);
      }

      // Close filter dropdown if clicking outside filter area
      const filterElement = document.getElementById('filter-container');
      if (filterElement && !filterElement.contains(event.target) && showFilters) {
        setShowFilters(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilters]);

  // Filter data based on search query and relationship types
  useEffect(() => {
    if (!graphData.nodes.length) return;

    let filteredNodes = [...graphData.nodes];
    let filteredLinks = [...graphData.links];

    // Filter by search query
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filteredNodes = filteredNodes.filter(node =>
        node.name.toLowerCase().includes(lowerQuery)
      );

      // Update search results for dropdown
      setSearchResults(filteredNodes);

      const filteredNodeIds = new Set(filteredNodes.map(node => node.id));
      filteredLinks = filteredLinks.filter(link => {
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
        const targetId = typeof link.target === 'object' ? link.target.id : link.target;
        return filteredNodeIds.has(sourceId) && filteredNodeIds.has(targetId);
      });
    } else {
      setSearchResults([]);
    }

    // Filter by relationship types
    if (selectedRelationshipTypes.length > 0) {
      filteredLinks = filteredLinks.filter(link =>
        selectedRelationshipTypes.includes(link.type)
      );

      const connectedNodeIds = new Set();
      filteredLinks.forEach(link => {
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
        const targetId = typeof link.target === 'object' ? link.target.id : link.target;
        connectedNodeIds.add(sourceId);
        connectedNodeIds.add(targetId);
      });

      // Always include the central node
      connectedNodeIds.add(selectedClient?.id || 'central');

      filteredNodes = filteredNodes.filter(node => connectedNodeIds.has(node.id));
    }

    setFilteredData({ nodes: filteredNodes, links: filteredLinks });

    // Render graph with the filtered data
    renderGraph(filteredNodes, filteredLinks);
  }, [graphData, searchQuery, selectedRelationshipTypes, selectedClient]);

  const toggleRelationshipType = (type) => {
    setSelectedRelationshipTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // Generate dummy data when selected client changes
  useEffect(() => {
    generateDummyData();
  }, [selectedClient]);

  // More realistic dummy data
  const generateDummyData = () => {
    setLoading(true);

    // More realistic client data
    const wealthyClients = [
      { name: "Robert Blackwell", role: "CEO, Blackwell Industries", wealth: "High", interests: ["Art", "Golf", "Philanthropy"] },
      { name: "Elizabeth Chen", role: "Founder, TechVision", wealth: "Very High", interests: ["Technology", "Education", "Travel"] },
      { name: "Michael Thornton", role: "Managing Partner, Thornton Capital", wealth: "High", interests: ["Wine", "Sailing", "Real Estate"] },
      { name: "Sophia Rodriguez", role: "CFO, Global Innovations", wealth: "Medium-High", interests: ["Tennis", "Classical Music", "Sustainable Investing"] },
      { name: "James Wilson", role: "Real Estate Developer", wealth: "High", interests: ["Architecture", "Horses", "Vintage Cars"] },
      { name: "Olivia Park", role: "Surgeon & Medical Investor", wealth: "Medium-High", interests: ["Medical Research", "Skiing", "Fine Dining"] },
      { name: "William Zhang", role: "Tech Entrepreneur", wealth: "Very High", interests: ["Startups", "Mountain Climbing", "Space Technology"] },
      { name: "Emma Goldstein", role: "Family Office Director", wealth: "High", interests: ["Art Collection", "Ballet", "Private Education"] },
      { name: "Daniel Morgan", role: "Retired Executive", wealth: "Medium-High", interests: ["Golf", "Fishing", "Grandchildren"] },
      { name: "Isabella Johnson", role: "Fashion Industry Executive", wealth: "High", interests: ["Fashion", "Travel", "Contemporary Art"] },
      { name: "Alexander Davis", role: "Private Equity Partner", wealth: "Very High", interests: ["Wine Collection", "Hunting", "Politics"] },
      { name: "Charlotte Williams", role: "Heiress & Philanthropist", wealth: "Very High", interests: ["Charity", "Horses", "Environmental Causes"] },
      { name: "Benjamin Taylor", role: "Energy Sector Executive", wealth: "High", interests: ["Boating", "Cigars", "Classic Literature"] },
      { name: "Amelia Brown", role: "Retail Chain Owner", wealth: "Medium-High", interests: ["Interior Design", "Yoga", "Organic Farming"] },
      { name: "Henry Martinez", role: "Construction & Development", wealth: "High", interests: ["Football", "Fishing", "Classic Cars"] },
      { name: "Victoria Lee", role: "Investment Banker", wealth: "High", interests: ["Marathon Running", "Photography", "Jazz Music"] },
      { name: "Thomas Anderson", role: "Software Company Founder", wealth: "Very High", interests: ["Aviation", "Scuba Diving", "Astronomy"] },
      { name: "Grace Miller", role: "Hotel Chain Owner", wealth: "High", interests: ["Gourmet Cooking", "Tennis", "Modern Art"] },
      { name: "Samuel White", role: "Legal Firm Partner", wealth: "Medium-High", interests: ["Golf", "Wine Tasting", "Classical Music"] },
      { name: "Ava Thompson", role: "Media Executive", wealth: "High", interests: ["Film Production", "Yoga", "Contemporary Literature"] }
    ];

    const nodes = [];
    const links = [];

    // Create relationship details
    const relationshipDetails = {
      "Family": ["Sibling", "Cousin", "In-law", "Parent/Child"],
      "Business": ["Co-founder", "Business Partner", "Investor", "Board Member"],
      "Friend": ["College Friend", "Social Circle", "Long-time Friend", "Recent Connection"],
      "Professional": ["Same Industry", "Former Colleague", "Industry Association", "Professional Network"],
      "Client Referral": ["Direct Referral", "Indirect Introduction", "Event Connection", "Mutual Client"],
      "Advisor": ["Legal Advisor", "Tax Consultant", "Financial Advisor", "Estate Planner"],
      "Investment Partner": ["Co-investor", "Fund Partner", "Real Estate Deal", "Startup Investment"],
      "Charity": ["Foundation Board", "Donor", "Fundraising Committee", "Charity Event"],
      "Club Member": ["Golf Club", "Yacht Club", "Country Club", "Private Club"],
      "Education": ["Alumni", "Same School", "Parent Connection", "Educational Foundation"]
    };
    
    // Add the selected client as the central node
    const centralNode = {
      id: selectedClient?.id || 'central',
      name: selectedClient?.name || 'Central Client',
      img: selectedClient?.img || 'https://randomuser.me/api/portraits/men/1.jpg',
      val: 25,
      group: 'primary',
      role: "Wealth Management Client",
      wealth: "Very High",
      interests: ["Investment", "Real Estate", "Philanthropy"],
      netWorth: "$25M-$50M",
      location: "New York, NY",
      clientSince: "2015",
      portfolioValue: "$32.7M",
      riskProfile: "Moderate"
    };

    nodes.push(centralNode);

    // Generate related clients with fixed positions in a circular layout
    const numRelatedClients = 18; // Fixed number for better layout

    for (let i = 0; i < numRelatedClients; i++) {
      const clientData = wealthyClients[i % wealthyClients.length];
      const gender = Math.random() > 0.5 ? 'men' : 'women';
      const imgId = Math.floor(Math.random() * 70) + 1;

      // Calculate position in a circle around the center
      const angle = (Math.PI * 2 * i) / numRelatedClients;
      const radius = 300; // Fixed radius for stable layout
      const x = 500 + Math.cos(angle) * radius;
      const y = 500 + Math.sin(angle) * radius;

      const node = {
        id: `client-${i}`,
        name: clientData.name,
        img: `https://randomuser.me/api/portraits/${gender}/${imgId}.jpg`,
        val: 20,
        group: Math.random() > 0.7 ? 'important' : 'regular',
        x: x,
        y: y,
        role: clientData.role,
        wealth: clientData.wealth,
        interests: clientData.interests,
        netWorth: ["$5M-$10M", "$10M-$25M", "$25M-$50M", "$50M+"][Math.floor(Math.random() * 4)],
        location: ["New York, NY", "San Francisco, CA", "Chicago, IL", "Miami, FL", "Boston, MA"][Math.floor(Math.random() * 5)],
        clientSince: ["2010", "2012", "2015", "2018", "2020"][Math.floor(Math.random() * 5)],
        portfolioValue: `$${(Math.random() * 40 + 5).toFixed(1)}M`,
        riskProfile: ["Conservative", "Moderate", "Aggressive"][Math.floor(Math.random() * 3)]
      };

      nodes.push(node);



      // Create a link from the central node to this node
      if (Math.random() > 0.2) {
        const relationshipType = relationshipTypes[Math.floor(Math.random() * relationshipTypes.length)];
        const detailOptions = relationshipDetails[relationshipType];
        const detail = detailOptions[Math.floor(Math.random() * detailOptions.length)];

        links.push({
          source: centralNode.id,
          target: node.id,
          type: relationshipType,
          detail: detail,
          strength: Math.random() * 0.9 + 0.1,
          notes: `Connected through ${detail.toLowerCase()}. Relationship established in 20${Math.floor(Math.random() * 10) + 10}.`,
          lastContact: `${Math.floor(Math.random() * 11) + 1} ${Math.random() > 0.5 ? 'weeks' : 'months'} ago`,
          interactions: Math.floor(Math.random() * 20) + 1
        });
      }
    }

    // Add connections between other nodes
    for (let i = 0; i < numRelatedClients / 2; i++) {
      const sourceIndex = Math.floor(Math.random() * (nodes.length - 1)) + 1;
      let targetIndex;
      do {
        targetIndex = Math.floor(Math.random() * (nodes.length - 1)) + 1;
      } while (targetIndex === sourceIndex);

      const relationshipType = relationshipTypes[Math.floor(Math.random() * relationshipTypes.length)];
      const detailOptions = relationshipDetails[relationshipType];
      const detail = detailOptions[Math.floor(Math.random() * detailOptions.length)];

      links.push({
        source: nodes[sourceIndex].id,
        target: nodes[targetIndex].id,
        type: relationshipType,
        detail: detail,
        strength: Math.random() * 0.5 + 0.1,
        notes: `Connected through ${detail.toLowerCase()}. Relationship established in 20${Math.floor(Math.random() * 10) + 10}.`,
        lastContact: `${Math.floor(Math.random() * 11) + 1} ${Math.random() > 0.5 ? 'weeks' : 'months'} ago`,
        interactions: Math.floor(Math.random() * 10) + 1
      });
    }

    setGraphData({ nodes, links });
    setFilteredData({ nodes, links });
    setLoading(false);

    // Initial graph render
    setTimeout(() => {
      renderGraph(nodes, links);
    }, 100);
  };

  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    const graphContainer = document.getElementById("graph-container");

    if (!isFullscreen) {
      if (graphContainer.requestFullscreen) {
        graphContainer.requestFullscreen();
      } else if (graphContainer.webkitRequestFullscreen) {
        graphContainer.webkitRequestFullscreen();
      } else if (graphContainer.msRequestFullscreen) {
        graphContainer.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }

    setIsFullscreen(!isFullscreen);

    // Redraw graph after fullscreen toggle
    setTimeout(() => {
      renderGraph(filteredData.nodes, filteredData.links);
    }, 300);
  };

  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Get icon for relationship type
  const getRelationshipIcon = (type) => {
    switch (type) {
      case 'Family': return <Home size={14} />;
      case 'Business': return <Briefcase size={14} />;
      case 'Friend': return <Heart size={14} />;
      case 'Professional': return <Users size={14} />;
      case 'Client Referral': return <Users size={14} />;
      case 'Advisor': return <Book size={14} />;
      case 'Investment Partner': return <DollarSign size={14} />;
      case 'Charity': return <Heart size={14} />;
      case 'Club Member': return <Coffee size={14} />;
      case 'Education': return <Book size={14} />;
      default: return <Globe size={14} />;
    }
  };

  // D3 visualization with fixed positions
  const renderGraph = (nodes, links) => {
    if (loading || !nodes.length) return;

    // Clear any existing SVG
    d3.select("#relationship-graph").selectAll("*").remove();

    const container = document.getElementById("relationship-graph");
    const width = container.clientWidth;
    const height = container.clientHeight;

    const svg = d3.select("#relationship-graph")
      .append("svg")
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("viewBox", `0 0 1000 1000`)
      .attr("preserveAspectRatio", "xMidYMid meet");

    // Create a group for the graph
    const g = svg.append("g");

    // Add zoom functionality
    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
      });

    svg.call(zoom);

    // Center the view
    svg.call(zoom.transform, d3.zoomIdentity.translate(width / 2 - 500, height / 2 - 500).scale(0.8));

    // Process links to use actual node objects
    const nodeMap = {};
    nodes.forEach(node => {
      nodeMap[node.id] = node;
    });

    const processedLinks = links.map(link => ({
      ...link,
      source: nodeMap[typeof link.source === 'object' ? link.source.id : link.source],
      target: nodeMap[typeof link.target === 'object' ? link.target.id : link.target]
    })).filter(link => link.source && link.target);

    // Create links
    const linkElements = g.selectAll(".link")
      .data(processedLinks)
      .enter()
      .append("line")
      .attr("class", "link")
      .attr("stroke", d => colorScale(d.type))
      .attr("stroke-width", 2)
      .attr("opacity", 0.6)
      .attr("x1", d => d.source.x)
      .attr("y1", d => d.source.y)
      .attr("x2", d => d.target.x)
      .attr("y2", d => d.target.y)
      .on("mouseover", (event, d) => {
        // Highlight this link
        d3.select(event.currentTarget)
          .attr("stroke-width", 4)
          .attr("opacity", 1);
      })
      .on("mouseout", (event, d) => {
        // Reset link style
        d3.select(event.currentTarget)
          .attr("stroke-width", 2)
          .attr("opacity", 0.6);
      });

    // Create node groups
    const nodeElements = g.selectAll(".node")
      .data(nodes)
      .enter()
      .append("g")
      .attr("class", "node")
      .attr("transform", d => `translate(${d.x}, ${d.y})`)
      .on("click", (event, d) => {
        focusOnClient(d.id);
      })
      .on("mouseover", (event, d) => {
        setHighlightedNode(d);

        // Find connections for this node
        const connections = processedLinks.filter(link =>
          link.source.id === d.id || link.target.id === d.id
        ).map(link => {
          const otherNode = link.source.id === d.id ? link.target : link.source;
          return {
            node: otherNode,
            relationship: link.type,
            detail: link.detail,
            notes: link.notes,
            lastContact: link.lastContact,
            interactions: link.interactions
          };
        });

        setHighlightedConnections(connections);

        // Highlight connected links and nodes
        linkElements
          .attr("opacity", link =>
            (link.source.id === d.id || link.target.id === d.id) ? 1 : 0.1
          )
          .attr("stroke-width", link =>
            (link.source.id === d.id || link.target.id === d.id) ? 3 : 1
          );

        nodeElements
          .attr("opacity", node => {
            if (node.id === d.id) return 1;
            return connections.some(conn => conn.node.id === node.id) ? 1 : 0.3;
          });
      })
      .on("mouseout", () => {
        // Don't clear highlighted node immediately to allow clicking on detail panel
        setTimeout(() => {
          if (!document.querySelector(".node-detail-panel:hover")) {
            setHighlightedNode(null);
            setHighlightedConnections([]);

            // Reset highlights
            linkElements
              .attr("opacity", 0.6)
              .attr("stroke-width", 2);

            nodeElements
              .attr("opacity", 1);
          }
        }, 100);
      })
      .call(d3.drag()
        .on("start", function (event, d) {
          // No need to restart simulation as we're using fixed positions
        })
        .on("drag", function (event, d) {
          // Update node position directly
          d.x = event.x;
          d.y = event.y;
          d3.select(this).attr("transform", `translate(${d.x}, ${d.y})`);

          // Update connected links
          linkElements
            .filter(link => link.source.id === d.id || link.target.id === d.id)
            .attr("x1", link => link.source.id === d.id ? d.x : link.source.x)
            .attr("y1", link => link.source.id === d.id ? d.y : link.source.y)
            .attr("x2", link => link.target.id === d.id ? d.x : link.target.x)
            .attr("y2", link => link.target.id === d.id ? d.y : link.target.y);
        })
        .on("end", function (event, d) {
          // No need to stop simulation as we're using fixed positions
        })
      );

    // Add node circles
    nodeElements.append("circle")
      .attr("r", d => d.val)
      .attr("fill", d => d.group === 'primary' ? '#ff6b6b' : (d.group === 'important' ? '#4dabf7' : '#74c0fc'))
      .attr("stroke", "white")
      .attr("stroke-width", 2);

    // Add node images using clipPath
    nodeElements.each(function (d) {
      const node = d3.select(this);

      // Create unique clip path ID
      const clipId = `clip-${d.id}-${Date.now()}`;

      // Add clip path definition
      svg.append("defs")
        .append("clipPath")
        .attr("id", clipId)
        .append("circle")
        .attr("r", d.val * 0.85);

      // Add image with clip path
      node.append("image")
        .attr("xlink:href", d.img)
        .attr("x", -d.val * 0.85)
        .attr("y", -d.val * 0.85)
        .attr("width", d.val * 1.7)
        .attr("height", d.val * 1.7)
        .attr("clip-path", `url(#${clipId})`);
    });

    // Add node labels
    nodeElements.append("text")
      .text(d => d.name)
      .attr("text-anchor", "middle")
      .attr("dy", d => d.val + 15)
      .attr("font-size", "12px")
      .attr("fill", "black")
      .attr("stroke", "white")
      .attr("stroke-width", 0.5)
      .attr("paint-order", "stroke");
  };

  // Calculate network statistics
  useEffect(() => {
    if (!filteredData.nodes.length) return;

    // Calculate connection counts for each node
    const connectionCounts = {};
    filteredData.nodes.forEach(node => {
      connectionCounts[node.id] = 0;
    });

    // Count relationships by type
    const relationshipCounts = {};

    filteredData.links.forEach(link => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;

      connectionCounts[sourceId] = (connectionCounts[sourceId] || 0) + 1;
      connectionCounts[targetId] = (connectionCounts[targetId] || 0) + 1;

      relationshipCounts[link.type] = (relationshipCounts[link.type] || 0) + 1;
    });

    let maxConnections = 0;
    let mostConnectedClientId = null;

    Object.entries(connectionCounts).forEach(([nodeId, count]) => {
      if (count > maxConnections) {
        maxConnections = count;
        mostConnectedClientId = nodeId;
      }
    });

    const mostConnectedClient = filteredData.nodes.find(node => node.id === mostConnectedClientId);

    let strongestRelationship = null;
    let maxStrength = 0;

    filteredData.links.forEach(link => {
      if (link.strength > maxStrength) {
        maxStrength = link.strength;
        strongestRelationship = link;
      }
    });

    const totalConnections = filteredData.links.length;
    const totalNodes = filteredData.nodes.length;
    const avgConnectionsPerClient = totalNodes > 0 ? totalConnections / totalNodes : 0;

    setNetworkStats({
      totalConnections,
      totalNodes,
      avgConnectionsPerClient,
      mostConnectedClient,
      strongestRelationship,
      relationshipCounts,
      connectionCounts
    });
  }, [filteredData]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      renderGraph(filteredData.nodes, filteredData.links);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [filteredData]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim()) {
      setShowSearchResults(true);
    } else {
      setShowSearchResults(false);
    }
  };

  const handleSelectSearchResult = (client) => {
    setSearchQuery(client.name);
    setShowSearchResults(false);
    focusOnClient(client.id);
  };

  if (loading) {
    return <LoadingState message="Loading relationship network..." />;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Client Relationship Network</h2>
      <p className="text-gray-600 mb-6">
        Visualize connections between clients and identify relationship patterns to uncover new opportunities.
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {relationshipTypes.map(type => (
          <div key={type} className="flex items-center">
            <span
              className="w-3 h-3 rounded-full mr-1"
              style={{ backgroundColor: colorScale(type) }}
            ></span>
            <span className="text-xs text-gray-700">{type}</span>
          </div>
        ))}
      </div>

      <div className="mb-6">
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div id="search-container" className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={18} className="text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search clients by name..."
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            {searchQuery && (
              <div className="absolute inset-y-0 right-0 flex items-center">
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setShowSearchResults(false);
                    resetFocus();
                  }}
                  className="p-1 mr-1 rounded-full text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              </div>
            )}

            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-auto">
                <ul className="py-1">
                  {searchResults.map(client => (
                    <li
                      key={client.id}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                      onClick={() => handleSelectSearchResult(client)}
                    >
                      <img
                        src={client.img}
                        alt={client.name}
                        className="w-8 h-8 rounded-full mr-2"
                      />
                      <div>
                        <div className="text-sm font-medium">{client.name}</div>
                        <div className="text-xs text-gray-500">{client.role}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Client Focus Dropdown */}
          <div className="w-full md:w-64">

            <div className="relative border border-gray-300 rounded-md">
              <select
                id="clientFocus"
                value={focusedClient || ''}
                onChange={(e) => {
                  if (e.target.value) {
                    focusOnClient(e.target.value);
                  } else {
                    resetFocus();
                  }
                }}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md appearance-none"
              >
                <option value="">All Clients</option>
                {graphData.nodes.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <ChevronDown size={16} className="text-gray-400" />
              </div>
            </div>
          </div>

          {/* Filter Button */}
          <div id="filter-container" className="relative">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center px-4 py-2 border rounded-md text-sm font-medium bg-white text-gray-700 border-gray-300 hover:bg-gray-50 h-[38px]"
            >
              <Filter size={18} className="mr-2" />
              Filter Relationships
            </button>

            {/* Filter Dropdown */}
            {showFilters && (
              <div className="absolute z-20 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-50">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Filter by Relationship Type</h3>
                <div className="flex flex-wrap gap-2">
                  {relationshipTypes.map(type => (
                    <button
                      key={type}
                      onClick={() => toggleRelationshipType(type)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedRelationshipTypes.includes(type)
                        ? 'bg-blue-100 text-primaryDark border border-blue-300'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                        }`}
                    >
                      <span
                        className="inline-block w-2 h-2 rounded-full mr-1"
                        style={{ backgroundColor: colorScale(type) }}
                      ></span>
                      {type}
                    </button>
                  ))}
                </div>

                <div className="flex justify-between mt-4">
                  <button
                    onClick={() => setSelectedRelationshipTypes([])}
                    className="text-xs text-primaryDark hover:text-primaryDark"
                  >
                    Clear Filters
                  </button>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* D3 Graph Container with Fullscreen Button */}
      <div
        id="graph-container"
        className={`relative border border-gray-200 rounded-lg overflow-hidden transition-all duration-300 ${isFullscreen ? 'fixed inset-0 z-50 bg-white' : 'h-[600px]'}`}
      >
        <div
          id="relationship-graph"
          className="w-full h-full"
        ></div>

        <div className="absolute top-4 right-4 flex gap-2 z-10">
          <button
            onClick={toggleFullscreen}
            className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            title={isFullscreen ? "Exit fullscreen" : "View fullscreen"}
          >
            {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
          </button>

          <button
            onClick={resetFocus}
            className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            title="Reset view"
          >
            <X size={20} />
          </button>
        </div>

        {/* Node Detail Panel */}
        {highlightedNode && (
          <div className="node-detail-panel absolute bottom-4 right-4 bg-white p-4 rounded-lg shadow-lg border border-gray-200 max-w-md z-10">
            <div className="flex items-center mb-3 border-b pb-3">
              <img
                src={highlightedNode.img}
                alt={highlightedNode.name}
                className="w-16 h-16 rounded-full mr-3 border-2 border-white shadow-sm"
              />
              <div>
                <h4 className="font-medium text-gray-900 text-lg">{highlightedNode.name}</h4>
                <p className="text-sm text-gray-600">{highlightedNode.role}</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-primaryDark rounded-full">
                    {highlightedNode.wealth} Net Worth
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
              <div>
                <p className="text-gray-500">Location</p>
                <p className="font-medium">{highlightedNode.location}</p>
              </div>
              <div>
                <p className="text-gray-500">Client Since</p>
                <p className="font-medium">{highlightedNode.clientSince}</p>
              </div>
              <div>
                <p className="text-gray-500">Portfolio Value</p>
                <p className="font-medium">{highlightedNode.portfolioValue}</p>
              </div>
              <div>
                <p className="text-gray-500">Risk Profile</p>
                <p className="font-medium">{highlightedNode.riskProfile}</p>
              </div>
            </div>

            <div className="mb-3">
              <p className="text-gray-500 text-sm">Interests</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {highlightedNode.interests.map((interest, index) => (
                  <span key={index} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full">
                    {interest}
                  </span>
                ))}
              </div>
            </div>

            {highlightedConnections.length > 0 && (
              <div>
                <h5 className="font-medium text-gray-800 mb-2">Connections ({highlightedConnections.length})</h5>
                <div className="max-h-40 overflow-y-auto pr-1">
                  {highlightedConnections.map((connection, index) => (
                    <div key={index} className="mb-2 pb-2 border-b border-gray-100 last:border-0">
                      <div className="flex items-center">
                        <img
                          src={connection.node.img}
                          alt={connection.node.name}
                          className="w-8 h-8 rounded-full mr-2"
                        />
                        <div>
                          <p className="font-medium text-sm">{connection.node.name}</p>
                          <div className="flex items-center">
                            <span
                              className="w-2 h-2 rounded-full mr-1"
                              style={{ backgroundColor: colorScale(connection.relationship) }}
                            ></span>
                            <span className="text-xs text-gray-600">{connection.relationship}: {connection.detail}</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-1 pl-10 text-xs text-gray-500">
                        <p>Last contact: {connection.lastContact}</p>
                        <p>{connection.interactions} interactions</p>
                        <p className="italic mt-1">{connection.notes}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Network Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-primaryDark text-sm font-medium mb-1">Total Connections</div>
            <div className="text-xl font-bold text-gray-800">
              {networkStats.totalConnections}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Avg. {networkStats.avgConnectionsPerClient.toFixed(1)} per client
            </div>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-green-600 text-sm font-medium mb-1">Most Connected</div>
            <div className="text-xl font-bold text-gray-800 truncate">
              {networkStats.mostConnectedClient?.name || 'N/A'}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {networkStats.connectionCounts[networkStats.mostConnectedClient?.id] || 0} connections
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-purple-600 text-sm font-medium mb-1">Top Relationship Type</div>
            {Object.entries(networkStats.relationshipCounts).length > 0 ? (
              <>
                <div className="text-xl font-bold text-gray-800">
                  {Object.entries(networkStats.relationshipCounts)
                    .sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {Object.entries(networkStats.relationshipCounts)
                    .sort((a, b) => b[1] - a[1])[0]?.[1] || 0} connections
                </div>
              </>
            ) : (
              <div className="text-xl font-bold text-gray-800">N/A</div>
            )}
          </div>

          <div className="bg-amber-50 rounded-lg p-4">
            <div className="text-amber-600 text-sm font-medium mb-1">Network Density</div>
            <div className="text-xl font-bold text-gray-800">
              {networkStats.totalConnections > 0 && networkStats.totalNodes > 1
                ? (networkStats.totalConnections / (networkStats.totalNodes * (networkStats.totalNodes - 1) / 2) * 100).toFixed(1)
                : 0}%
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Of all possible connections
            </div>
          </div>
        </div>

        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Relationship Distribution</h4>
          <div className="h-12 bg-gray-100 rounded-lg overflow-hidden flex">
            {Object.entries(networkStats.relationshipCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([type, count], index) => {
                const percentage = networkStats.totalConnections > 0
                  ? (count / networkStats.totalConnections) * 100
                  : 0;
                return (
                  <div
                    key={type}
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: colorScale(type)
                    }}
                    className="h-full relative group"
                    title={`${type}: ${count} (${percentage.toFixed(1)}%)`}
                  >
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black bg-opacity-50 transition-opacity">
                      <span className="text-white text-xs font-medium">{percentage.toFixed(0)}%</span>
                    </div>
                  </div>
                );
              })}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2 mt-2">
            {Object.entries(networkStats.relationshipCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([type, count]) => {
                const percentage = networkStats.totalConnections > 0
                  ? (count / networkStats.totalConnections) * 100
                  : 0;
                return (
                  <div key={type} className="flex items-center text-xs">
                    <span
                      className="w-2 h-2 rounded-full mr-1"
                      style={{ backgroundColor: colorScale(type) }}
                    ></span>
                    <span className="text-gray-700">{type}:</span>
                    <span className="font-medium ml-1">{count}</span>
                    <span className="text-gray-500 ml-1">({percentage.toFixed(1)}%)</span>
                  </div>
                );
              })}
          </div>
        </div>

        <div className="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Network Opportunities</h4>
          <ul className="space-y-2">
            {networkStats.totalNodes > 0 && (
              <>
                {networkStats.mostConnectedClient && networkStats.mostConnectedClient.id !== 'central' && (
                  <li className="flex items-start text-sm">
                    <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5">
                      <Users size={12} className="text-primaryDark" />
                    </div>
                    <span>
                      <strong>{networkStats.mostConnectedClient.name}</strong> is highly connected in this network.
                      Consider leveraging this relationship for introductions and referrals to {networkStats.mostConnectedClient.role.toLowerCase()} contacts.
                    </span>
                  </li>
                )}

                {Object.entries(networkStats.relationshipCounts).length > 0 && (
                  <li className="flex items-start text-sm">
                    <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                      <Filter size={12} className="text-green-600" />
                    </div>
                    <span>
                      <strong>{Object.entries(networkStats.relationshipCounts)
                        .sort((a, b) => b[1] - a[1])[0]?.[0]}</strong> is the most common relationship type.
                      Consider developing more diverse relationship types to strengthen the network.
                    </span>
                  </li>
                )}

                {networkStats.totalNodes > 5 && networkStats.totalConnections / networkStats.totalNodes < 2 && (
                  <li className="flex items-start text-sm">
                    <div className="flex-shrink-0 h-5 w-5 rounded-full bg-amber-100 flex items-center justify-center mr-2 mt-0.5">
                      <Users size={12} className="text-amber-600" />
                    </div>
                    <span>
                      This network has relatively few connections between clients.
                      There may be opportunities to introduce clients to each other and strengthen the community.
                    </span>
                  </li>
                )}

                {/* New opportunity insight based on wealth levels */}
                <li className="flex items-start text-sm">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-purple-100 flex items-center justify-center mr-2 mt-0.5">
                    <DollarSign size={12} className="text-purple-600" />
                  </div>
                  <span>
                    <strong>{filteredData.nodes.filter(node => node.wealth === "Very High").length}</strong> clients have "Very High" net worth.
                    Consider organizing an exclusive event for these clients to strengthen relationships and generate referrals.
                  </span>
                </li>

                {/* New opportunity insight based on interests */}
                <li className="flex items-start text-sm">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                    <Heart size={12} className="text-indigo-600" />
                  </div>
                  <span>
                    <strong>Philanthropy</strong> is a common interest among several clients.
                    Consider organizing a charity event or impact investing seminar to engage these clients.
                  </span>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>

      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-600">
          <span className="font-medium">{filteredData.nodes.length}</span> clients with
          <span className="font-medium"> {filteredData.links.length}</span> relationships
        </div>
        <div className="flex gap-2">
          <button
            onClick={resetFocus}
            className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200"
          >
            Reset View
          </button>
          <button
            onClick={generateDummyData}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
          >
            Refresh Data
          </button>
        </div>
      </div>

      {/* Instructions tooltip */}
      <div className="mt-4 bg-blue-50 p-3 rounded-md text-sm text-primaryDark">
        <p className="font-medium mb-1">Tips:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Drag nodes to reposition them</li>
          <li>Hover over nodes to see detailed client information</li>
          <li>Click on a node to focus on that client's connections</li>
          <li>Use the fullscreen button for a larger view</li>
          <li>Filter by relationship type to focus on specific connections</li>
        </ul>
      </div>
    </div>
  );
};

export default ClientRelationshipGraph;